import React, { Component } from 'react';
import {
  arrayOf,
  objectOf,
  array,
  string,
  func,
  bool,
  object,
} from 'prop-types';
import styled from 'styled-components';
import { withRouter } from 'react-router-dom';
import { toLower, isEmpty, keys } from 'lodash';

import { withGroupContext } from 'context/groupContext';

import DataFetcher from 'components/DataFetcher/DataFetcher';
import Annotations from 'components/Annotations/Annotations';
import LineChart from 'components/LineChart/LineChart';
import LineChartLegend from 'components/LineChartLegend/LineChartLegend';
import ButtonGroup from 'components/ButtonGroup/ButtonGroup';

import saveSvgToImage from 'helpers/saveSvgToImage';
import createApiUrl from 'helpers/createApiUrl';
import PlaceholderSVG from './placeholderChart.svg';

const Section = styled.section`
  padding: 2em;
`;

const ChartHeader = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 1em;
`;

const Title = styled.h2`
  font-size: 1em;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
`;

const Placeholder = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Content = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  height: 100%;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

export class LineChartWrapper extends Component {
  state = {
    showReference: true,
  };

  handleReferenceToggle = () => {
    this.setState({ showReference: !this.state.showReference });
  };

  /**
   * Call helper function to create an export of the SVG (based on
   * its id) and name it according to the active groups.
   */
  handleExport = () => {
    const groupIdsString = toLower(keys(this.props.scores).join('-'));
    saveSvgToImage('#LineChart', `keencorp-line-chart-${groupIdsString}.png`);
  };

  render() {
    const { showReference } = this.state;
    const {
      scores,
      editModeEnabled,
      onEditModeClick,
      onNodeClick,
      onSmoothDataToggle,
      showSmoothData,
      annotationIds,
      clickedNode,
      increaseCount,
      clearClickedNode,
    } = this.props;

    /**
     * Check whether or not the data is valid in order to render the chart
     * or show a placeholder wit no data message instead.
     * Loop over the groups that should be active and check if they contain
     * data. If not filter them out of the list.
     */
    const groupIds = keys(scores);
    const groupsWithData = groupIds.filter(id => !isEmpty(scores[id]));
    const hasNoData = groupsWithData.length < 1;

    return (
      <Section>
        <ChartHeader>
          <Title>KeenCorp index over time</Title>
          <ButtonGroup
            editModeEnabled={editModeEnabled}
            onEditModeClick={onEditModeClick}
            onExportClick={this.handleExport}
            onReferenceToggle={this.handleReferenceToggle}
            showReference={showReference}
            onSmoothDataToggle={onSmoothDataToggle}
            showSmoothData={showSmoothData}
          />
        </ChartHeader>
        <div id="LineChart">
          <LineChartLegend
            showReference={showReference}
            groupsWithData={groupsWithData}
            groupIds={groupIds}
          />
          {hasNoData && (
            <Placeholder>
              <Content>
                <p>Unfortunately we could not retrieve data for this period.</p>
              </Content>
              <PlaceholderSVG />
            </Placeholder>
          )}
          {!hasNoData && (
            <LineChart
              scores={this.props.scores}
              showReference={showReference}
              groupsWithData={groupsWithData}
              onNodeClick={onNodeClick}
            />
          )}
        </div>
        {annotationIds && (
          <DataFetcher
            url={createApiUrl({
              path: 'request_annotations',
              request: {
                annotations: annotationIds,
              },
            })}
            render={annotationsData => {
              const annotationList =
                annotationsData &&
                annotationsData.data &&
                annotationsData.data.annotations;

              return (
                <Annotations
                  editModeEnabled={editModeEnabled}
                  clickedNode={clickedNode}
                  annotationList={annotationList}
                  increaseCount={increaseCount}
                  clearClickedNode={clearClickedNode}
                  groupsWithData={groupsWithData}
                  showReference={showReference}
                />
              );
            }}
          />
        )}
      </Section>
    );
  }
}

LineChartWrapper.propTypes = {
  activeGroupIds: arrayOf(string).isRequired,
  scores: objectOf(array).isRequired,
  editModeEnabled: bool,
  onEditModeClick: func.isRequired,
  onNodeClick: func.isRequired,
  showSmoothData: bool,
  onSmoothDataToggle: func.isRequired,
  annotationIds: array,
  clickedNode: object,
  increaseCount: func.isRequired,
  clearClickedNode: func.isRequired,
};

export default withRouter(withGroupContext(LineChartWrapper));



// WEBPACK FOOTER //
// ./src/components/LineChartWrapper/LineChartWrapper.js